/**
 * API Interceptor for handling authentication and token expiration
 */

let authContext = null;

/**
 * Initialize the API interceptor with auth context
 * @param {object} auth - Auth context from useAuth hook
 */
export function initializeApiInterceptor(auth) {
    authContext = auth;
}

/**
 * Enhanced fetch wrapper that handles token expiration
 * @param {string} url - API endpoint
 * @param {object} options - Fetch options
 * @returns {Promise<Response>} - Fetch response
 */
export async function apiRequest(url, options = {}) {
    // Add auth header if token exists
    if (authContext?.token) {
        options.headers = {
            ...options.headers,
            'Authorization': `Bearer ${authContext.token}`
        };
    }
    
    try {
        const response = await fetch(url, options);
        
        // Check for 401 Unauthorized (token expired or invalid)
        if (response.status === 401) {
            console.warn('API request returned 401 - token may be expired');
            
            // Logout user if we have auth context
            if (authContext?.logout) {
                authContext.logout();
                
                // Redirect to login page if we're on an admin page
                if (window.location.pathname.startsWith('/admin') && 
                    !window.location.pathname.includes('/login')) {
                    window.location.href = '/admin/login';
                }
            }
        }
        
        return response;
    } catch (error) {
        console.error('API request failed:', error);
        throw error;
    }
}

/**
 * Helper function to make GET requests with auth handling
 * @param {string} url - API endpoint
 * @param {object} options - Additional fetch options
 * @returns {Promise<Response>} - Fetch response
 */
export async function apiGet(url, options = {}) {
    return apiRequest(url, {
        method: 'GET',
        ...options
    });
}

/**
 * Helper function to make POST requests with auth handling
 * @param {string} url - API endpoint
 * @param {object} data - Request body data
 * @param {object} options - Additional fetch options
 * @returns {Promise<Response>} - Fetch response
 */
export async function apiPost(url, data, options = {}) {
    return apiRequest(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...options.headers
        },
        body: JSON.stringify(data),
        ...options
    });
}

/**
 * Helper function to make PUT requests with auth handling
 * @param {string} url - API endpoint
 * @param {object} data - Request body data
 * @param {object} options - Additional fetch options
 * @returns {Promise<Response>} - Fetch response
 */
export async function apiPut(url, data, options = {}) {
    return apiRequest(url, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            ...options.headers
        },
        body: JSON.stringify(data),
        ...options
    });
}

/**
 * Helper function to make DELETE requests with auth handling
 * @param {string} url - API endpoint
 * @param {object} options - Additional fetch options
 * @returns {Promise<Response>} - Fetch response
 */
export async function apiDelete(url, options = {}) {
    return apiRequest(url, {
        method: 'DELETE',
        ...options
    });
}